<script setup>
/**
 * Minimal color picker showing the palette view (no header).
 *
 * The palette is the whole picker unless `allowCustom` is set. Everything other than that prop is
 * forwarded via `$attrs` to Quasar's `q-color`. See the
 * [Quasar q-color API](https://quasar.dev/vue-components/color-picker) for the full prop list.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Letting the user pick a color value from a palette
 * @see https://quasar.dev/vue-components/color-picker Quasar q-color API
 */
defineOptions({ name: 'HrColor', inheritAttrs: false });

const emit = defineEmits({
  /**
   * Fired after a palette swatch is picked, once the new value is out. A swatch is a whole choice,
   * where a spectrum release or a slider step is one adjustment of several, and q-color fires the
   * same `change` for all of them.
   */
  'swatch-pick': null,
});

defineProps({
  /**
   * The value is the reader's own rather than one of a set, so it may be any color. Adds the
   * sliders and the spectrum beside the palette, which still opens first.
   *
   * Off where the color is a role the palette defines; on where it only has to tell records apart
   * and a reader may already have chosen one the palette does not hold, such as a tag's.
   */
  allowCustom: {
    type: Boolean,
    default: false,
  },
});

// Bubbles up from the swatch after q-color has already handled it, so the value is out by now.
const onClick = (evt) => {
  if (evt.target.closest('.q-color-picker__cube')) {
    emit('swatch-pick');
  }
};
</script>

<template>
  <q-color
    v-bind="$attrs"
    class="hr-color"
    default-view="palette"
    no-header
    :no-footer="!allowCustom"
    @click="onClick"
  />
</template>

<style lang="scss" scoped>

/*
 * Quasar paints the picker white and leaves its text, inputs and footer icons to inherit the page's
 * color, which in the dark theme is white on white.
 */
[data-theme] .hr-color {
  background: var(--app-surface-container-high);
  color: var(--app-on-surface);
  box-shadow: var(--app-elevation-3);
}

[data-theme] .hr-color :deep(.q-color-picker__tune-tab input) {
  border-color: var(--app-outline);
}

[data-theme] .hr-color :deep(.q-color-picker__sliders .q-slider__thumb) {
  color: var(--app-on-surface);
}

/* Quasar marks the inactive views with a black gradient, which reads as a hole on a dark surface. */
[data-theme] .hr-color :deep(.q-color-picker__footer .q-tab) {
  color: var(--app-on-surface-variant);
}

[data-theme] .hr-color :deep(.q-color-picker__footer .q-tab--inactive) {
  background: var(--app-surface-container-highest);
}

[data-theme] .hr-color :deep(.q-color-picker__footer .q-tab--active) {
  color: var(--app-primary);
  box-shadow: none;
}

</style>
